import { Plus, Trash2, Wand2, AlertTriangle } from "lucide-react";
import { Field, Section, Toggle, inputCls } from "./InvoiceFormSections";
import { VAT_RATES, splitLines, money, pctTableFor } from "./invoiceDefaults";

export function SplitSection({ f, set, rules, amountSource, bookingAmount }) {
  const sp = f.split;
  const setSplit = (patch) => {
    const split = { ...sp, ...patch };
    set({ ...f, split, lines: splitLines(rules, split, f.pax) });
  };
  return (
    <Section title="Kalem Bölme Kuralı" right={
      <button type="button" data-testid="inv-resplit" onClick={() => setSplit({})}
        className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-indigo-600 text-white text-[11px] font-semibold hover:bg-indigo-700 transition-colors">
        <Wand2 size={12} /> Kurala göre yeniden böl
      </button>}>
      <Field label="Kişi başı tutar" hint={amountSource?.endsWith("_disc") ? `Bókun fiyatından %${f.split.discount_pct || 0} kanal indirimi düşüldü: ${money(bookingAmount, f.currency)}` : amountSource === "bokun" ? `Bókun toplamı ${money(bookingAmount, f.currency)}` : amountSource === "bokun_linked" ? `Asıl Bókun rezervasyonundan ${money(bookingAmount, f.currency)}` : amountSource === "defter" ? `Defter toplamı ${money(bookingAmount, f.currency)}` : amountSource === "odenen" ? `Canlı sayfa T. Tutar / Ödenen ${money(bookingAmount, f.currency)}` : "Tutar bulunamadı, elle girin"}>
        <input data-testid="inv-per-person" type="number" step="0.01" className={inputCls} value={sp.per_person}
          onChange={(e) => setSplit({ per_person: e.target.value })} />
      </Field>
      <Field label="Kişi sayısı">
        <input data-testid="inv-pax" type="number" min="1" className={inputCls} value={f.pax}
          onChange={(e) => { const pax = e.target.value; set({ ...f, pax, lines: splitLines(rules, sp, pax) }); }} />
      </Field>
      <Field label="Tur türü">
        <select data-testid="inv-tier" className={inputCls} value={sp.tier} onChange={(e) => setSplit({ tier: e.target.value })}>
          {(rules.tiers || []).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </Field>
      {pctTableFor(rules, sp) ? (
        <Field label="Bölme"><div data-testid="inv-percent-mode" className="h-9 flex items-center text-[12px] font-semibold text-emerald-700">Kendi site · satış fiyatı oranla bölünür</div></Field>
      ) : (
        <Field label="Acenta payı %">
          <input data-testid="inv-commission-pct" type="number" step="0.1" className={inputCls} value={sp.commission_pct}
            onChange={(e) => setSplit({ commission_pct: e.target.value })} />
        </Field>
      )}
      <Field label="Transferli"><Toggle testId="inv-transfer" on={sp.transfer} onClick={() => setSplit({ transfer: !sp.transfer })} /></Field>
      <Field label="Alkollü"><Toggle testId="inv-alcohol" on={sp.alcohol} onClick={() => setSplit({ alcohol: !sp.alcohol })} /></Field>
    </Section>
  );
}

function LineRow({ l, i, onChange, onRemove, cur }) {
  const upd = (k, v) => onChange(i, { ...l, [k]: v });
  return (
    <tr data-testid={`inv-line-${i}`} className="border-t border-slate-100">
      <td className="py-1 pr-2"><input data-testid={`inv-line-desc-${i}`} className={inputCls} value={l.description} onChange={(e) => upd("description", e.target.value)} /></td>
      <td className="py-1 pr-2 w-20"><input data-testid={`inv-line-qty-${i}`} type="number" className={inputCls} value={l.quantity} onChange={(e) => upd("quantity", e.target.value)} /></td>
      <td className="py-1 pr-2 w-28"><input data-testid={`inv-line-unit-${i}`} type="number" step="0.01" className={inputCls} value={l.unit_gross} onChange={(e) => upd("unit_gross", e.target.value)} /></td>
      <td className="py-1 pr-2 w-24">
        <select data-testid={`inv-line-vat-${i}`} className={inputCls} value={l.vat_rate} onChange={(e) => upd("vat_rate", Number(e.target.value))}>
          {VAT_RATES.map((v) => <option key={v} value={v}>%{v}</option>)}
        </select>
      </td>
      <td data-testid={`inv-line-total-${i}`} className="py-1 pr-2 w-28 text-right tabular-nums font-semibold text-slate-700">{money((Number(l.quantity) || 0) * (Number(l.unit_gross) || 0), cur)}</td>
      <td className="py-1 w-8"><button type="button" data-testid={`inv-line-del-${i}`} onClick={() => onRemove(i)} className="text-slate-400 hover:text-red-600 transition-colors"><Trash2 size={14} /></button></td>
    </tr>
  );
}

export function hasNegative(f) {
  const lines = f.lines || [];
  return lines.some((l) => Number(l.unit_gross) < 0) || !lines.some((l) => Number(l.unit_gross) > 0);
}

export function LinesEditor({ f, set, bookingAmount }) {
  const lines = f.lines || [];
  const onChange = (i, l) => set({ ...f, lines: lines.map((x, j) => (j === i ? l : x)) });
  const onRemove = (i) => set({ ...f, lines: lines.filter((_, j) => j !== i) });
  const add = () => set({ ...f, lines: [...lines, { description: "", quantity: f.pax || 1, unit_gross: 0, vat_rate: 20 }] });
  const negative = lines.some((l) => Number(l.unit_gross) < 0);
  const sum = lines.reduce((a, l) => a + (Number(l.quantity) || 0) * (Number(l.unit_gross) || 0), 0);
  const diff = bookingAmount ? Math.round((sum - bookingAmount) * 100) / 100 : 0;
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="mb-1 flex items-center justify-between">
        <h4 className="text-[11px] font-bold uppercase tracking-[0.14em] text-indigo-700">Fatura Kalemleri <span className="normal-case tracking-normal font-medium text-slate-400">· birim fiyatlar KDV dahil, kişi başı</span></h4>
        <button type="button" data-testid="inv-line-add" onClick={add} className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 hover:underline"><Plus size={12} /> Satır ekle</button>
      </div>
      {negative && (
        <div data-testid="inv-negative-warning" className="mb-2 flex items-center gap-1.5 rounded-md bg-red-50 border border-red-200 px-2 py-1 text-[12px] text-red-700">
          <AlertTriangle size={13} /> Fiyat sabit kalemleri karşılamıyor (eksi satır var). Tutarları düzeltmeden fatura kaydedilemez.
        </div>
      )}
      {!negative && diff !== 0 && (
        <div data-testid="inv-diff-warning" className="mb-2 flex items-center gap-1.5 rounded-md bg-amber-50 border border-amber-200 px-2 py-1 text-[12px] text-amber-800">
          <AlertTriangle size={13} /> Kalem toplamı rezervasyon tutarından {money(Math.abs(diff), f.currency)} {diff > 0 ? "fazla" : "eksik"}.
        </div>
      )}
      <table className="w-full text-[12px]">
        <thead className="text-[10px] uppercase tracking-wider text-slate-500">
          <tr><th className="text-left font-semibold pb-1">Açıklama</th><th className="text-left font-semibold">Adet</th><th className="text-left font-semibold">Birim (KDV dahil)</th><th className="text-left font-semibold">KDV</th><th className="text-right font-semibold pr-2">Tutar</th><th /></tr>
        </thead>
        <tbody>{lines.map((l, i) => <LineRow key={i} l={l} i={i} onChange={onChange} onRemove={onRemove} cur={f.currency} />)}</tbody>
      </table>
    </section>
  );
}
